/**
 * Conversation Filter Helpers
 * ---------------------------
 * This file contains helper functions for filtering conversations in the frontend.
 * The filtering logic is designed to align with the backend SQL behavior to ensure
 * consistent results across the application.
 *
 * Key components:
 * 1. getValueFromConversation: Retrieves values from conversation objects, handling
 *    both top-level properties and nested attributes.
 * 2. matchesCondition: Evaluates a single filter condition against a value.
 * 3. matchesFilters: Evaluates a complete filter chain against a conversation.
 * 4. buildJsonLogicRule: Transforms evaluated filters into a JSON Logic frule that
 *    respects SQL-like operator precedence.
 *
 * Filter Structure:
 * -----------------
 * Each filter has the following structure:
 * {
 *   attributeKey: 'status',          // The attribute to filter on
 *   filterOperator: 'equal_to',      // The operator to use (equal_to, contains, etc.)
 *   values: ['open'],                // The values to compare against
 *   queryOperator: 'and'             // How this filter connects to the next one (and/or)
 * }
 *
 * Operator Precedence:
 * --------------------
 * The filter evaluation respects SQL-like operator precedence using JSON Logic:
 * https://www.postgresql.org/docs/17/sql-syntax-lexical.html#SQL-PRECEDENCE
 * 1. First evaluates individual conditions
 * 2. Then applies AND operators (groups consecutive AND conditions)
 * 3. Finally applies OR operators (connects AND groups with OR operations)
 *
 * This means that a filter chain like "A AND B OR C" is evaluated as "(A AND B) OR C",
 * and "A OR B AND C" is evaluated as "A OR (B AND C)".
 *
 * The implementation uses json-logic-js to apply these rules. The JsonLogic format is designed
 * to allow you to share rules (logic) between front-end and back-end code
 * Here we use json-logic-js to transform filter conditions into a nested JSON Logic structure that preserves proper
 * operator precedence, effectively mimicking SQL-like operator precedence.
 *
 * Conversation Object Structure:
 * -----------------------------
 * The conversation object can have:
 * 1. Top-level properties (status, priority, display_id, etc.)
 * 2. Nested properties in additional_attributes (browser_language, referer, etc.)
 * 3. Nested properties in custom_attributes (conversation_type, etc.)
 */
import { coerceToDate } from '@chatwoot/utils';
import jsonLogic from 'json-logic-js';

/**
 * Gets a value from a conversation based on the attribute key
 * @param {Object} conversation - The conversation object
 * @param {String} attributeKey - The attribute key to get the value for
 * @returns {*} - The value of the attribute
 *
 * This function handles various attribute locations:
 * 1. Direct properties on the conversation object (status, priority, etc.)
 * 2. Properties in conversation.additional_attributes (browser_language, referer, etc.)
 * 3. Properties in conversation.custom_attributes (conversation_type, etc.)
 */
const getValueFromConversation = (conversation, attributeKey) => {
  switch (attributeKey) {
    case 'status':
    case 'priority':
    case 'labels':
    case 'created_at':
    case 'last_activity_at':
      return conversation[attributeKey];
    case 'display_id':
      // Frontend uses 'id' but backend expects 'display_id'
      return conversation.display_id || conversation.id;
    case 'assignee_id':
      return conversation.meta?.assignee?.id;
    case 'inbox_id':
      return conversation.inbox_id;
    case 'team_id':
      return conversation.meta?.team?.id;
    case 'campaign_id':
      return conversation.campaign_id;
    case 'browser_language':
    case 'referer':
      return conversation.additional_attributes?.[attributeKey];
    default:
      // A custom attribute. false (a yes/no set to "No"), 0 and '' are values too: the backend
      // only takes as absent what `custom_attributes ->> 'key'` gives as NULL.
      return conversation.custom_attributes?.[attributeKey] ?? null;
  }
};

const CUSTOM_ATTRIBUTE = Symbol('customAttribute');

/**
 * Tells if an attribute key is a custom attribute, one getValueFromConversation reads from
 * custom_attributes. Asking its switch keeps it the one list of standard attributes.
 * @param {String} attributeKey - The attribute key
 * @returns {Boolean}
 */
const isCustomAttribute = attributeKey =>
  getValueFromConversation(
    { custom_attributes: { [attributeKey]: CUSTOM_ATTRIBUTE } },
    attributeKey
  ) === CUSTOM_ATTRIBUTE;

/**
 * Resolves the value from an input candidate
 * @param {*} candidate - The input value to resolve
 * @returns {*} - If the candidate is an object with an id property, returns the id;
 *                otherwise returns the candidate unchanged
 *
 * This helper function is used to normalize values, particularly when dealing with
 * objects that represent entities like users, teams, or inboxes where we want to
 * compare by ID rather than by the whole object.
 */
const resolveValue = candidate => {
  if (
    typeof candidate === 'object' &&
    candidate !== null &&
    'id' in candidate
  ) {
    return candidate.id;
  }

  return candidate;
};

/**
 * Checks if two values are equal in the context of filtering
 * @param {*} filterValue - The filterValue value
 * @param {*} conversationValue - The conversationValue value
 * @returns {Boolean} - Returns true if the values are considered equal according to filtering rules
 *
 * This function handles various equality scenarios:
 * 1. When both values are arrays (e.g. labels): matches if any filter value exists in the conversation array
 *    (mirrors the backend SQL `tag_id IN (...)` OR semantics)
 * 2. When filterValue is an array but conversationValue is not: checks if conversationValue is included in filterValue
 * 3. Otherwise: performs strict equality comparison
 */
const equalTo = (filterValue, conversationValue) => {
  if (Array.isArray(filterValue)) {
    if (filterValue.includes('all')) return true;
    if (filterValue === 'all') return true;

    if (Array.isArray(conversationValue)) {
      // For array values like labels, match if any filter value is present.
      // Mirrors the backend SQL `tag_id IN (...)` (OR semantics).
      return filterValue.some(val => conversationValue.includes(val));
    }

    if (!Array.isArray(conversationValue)) {
      return filterValue.includes(conversationValue);
    }
  }

  return conversationValue === filterValue;
};

/**
 * Checks if the filterValue value is contained within the conversationValue value
 * @param {*} filterValue - The value to look for
 * @param {*} conversationValue - The value to search within
 * @returns {Boolean} - Returns true if filterValue is contained within conversationValue
 *
 * This function performs case-insensitive string containment checks.
 * It only works with string values and returns false for non-string types.
 *
 * The filter pill and a saved view (filterQueryGenerator) send the terms as a list: it matches
 * when any term is contained, like the backend's `ILIKE ANY`. Its negation, does_not_contain,
 * becomes "no term is contained", like `NOT ILIKE ALL`.
 */
const contains = (filterValue, conversationValue) => {
  if (typeof conversationValue !== 'string') return false;
  const terms = Array.isArray(filterValue) ? filterValue : [filterValue];
  return terms.some(
    term =>
      typeof term === 'string' &&
      conversationValue.toLowerCase().includes(term.toLowerCase())
  );
};

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Gets the day of a date value as YYYY-MM-DD, in UTC
 * @param {*} value - A timestamp, a date string or a day ("2026-09-01")
 * @returns {String|null} - The day, or null when the value is not a date
 *
 * A day (a custom date attribute) is already the day. Anything else goes by its UTC day, the one
 * the backend's `::date` gives to a timestamp column. A timestamp out of the range of a Date
 * (a custom attribute saved by the API) comes from coerceToDate as an Invalid Date, not null,
 * and toISOString would throw.
 */
const dayOf = value => {
  if (typeof value === 'string' && DAY_ONLY.test(value)) return value;
  const date = coerceToDate(value);
  if (date === null || Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
};

/**
 * Compares two date values using a comparison function
 * @param {*} conversationValue - The conversation value to compare
 * @param {*} filterValue - The filter value to compare against
 * @param {Function} compareFn - The comparison function to apply
 * @returns {Boolean} - Returns true if the comparison succeeds, false otherwise
 */
const compareDates = (conversationValue, filterValue, compareFn) => {
  // In saved views, the filterValue might be returned as an Array
  // In conversation list, when filtering, the filterValue will be returned as a string
  const valueToCompare = Array.isArray(filterValue)
    ? filterValue[0]
    : filterValue;

  // A day without time compares days, like the backend: `(created_at)::date > '2026-09-01'`.
  // Comparing instants let in "after the 1st" a conversation from the 1st itself (and "between"
  // is sent as "> the day before"). The midnight from coerceToDate is also a day early west of
  // UTC: '2026-09-01' is parsed as UTC and then set to the local midnight of Aug 31.
  if (typeof valueToCompare === 'string' && DAY_ONLY.test(valueToCompare)) {
    const conversationDay = dayOf(conversationValue);
    if (conversationDay === null) return false;
    return compareFn(conversationDay, valueToCompare);
  }

  const conversationDate = coerceToDate(conversationValue);
  const filterDate = coerceToDate(valueToCompare);

  if (conversationDate === null || filterDate === null) return false;
  return compareFn(conversationDate, filterDate);
};

// A date with time, as the CustomAttribute.vue saves a date ("2026-09-01T03:00:00.000Z").
const DATE_TIME = /^\d{4}-\d{2}-\d{2}T/;

/**
 * Gets the day written in a custom attribute with a date and time
 * @param {*} value - The value of the custom attribute
 * @returns {*} - The day ('2026-09-01') of a date with time, or the value unchanged
 *
 * A custom attribute is text in custom_attributes, and the backend's `::date` takes the day
 * written in it, whatever the offset: '2026-09-01T23:30:00-05:00'::date is 2026-09-01, while
 * that instant is already Sep 2 in UTC. With "Z", as the CustomAttribute.vue saves it, both agree.
 */
const writtenDay = value =>
  typeof value === 'string' && DATE_TIME.test(value)
    ? value.slice(0, 10)
    : value;

// The cast the backend gives each type of custom attribute (FilterService::ATTRIBUTE_TYPES).
const CUSTOM_ATTRIBUTE_CASTS = {
  date: 'date',
  text: 'text',
  number: 'numeric',
  currency: 'numeric',
  percent: 'numeric',
  link: 'text',
  list: 'text',
  checkbox: 'boolean',
};

/**
 * Maps each conversation custom attribute to the cast the backend gives it
 * @param {Array} attributes - The conversation attributes (attributes/getConversationAttributes)
 * @returns {Object} - The cast of each attribute key
 */
export const customAttributeCasts = (attributes = []) =>
  Object.fromEntries(
    attributes.map(({ attributeKey, attributeDisplayType }) => [
      attributeKey,
      CUSTOM_ATTRIBUTE_CASTS[attributeDisplayType],
    ])
  );

// Postgres takes as a boolean these words, in any case or by a unique prefix, and 1 and 0.
const TRUE_WORDS = ['true', 'yes', 'on'];
const FALSE_WORDS = ['false', 'no', 'off'];

/**
 * Casts a filter value or the value of a custom attribute as the backend does
 * (custom_attribute_filter_helper.rb): text, link and list by LOWER, number as numeric, yes/no as
 * boolean and date as date
 * @param {*} value - The value to cast
 * @param {String} cast - text, numeric, boolean or date
 * @returns {*} - The cast value, or null when the backend matches nothing with it
 *
 * The backend casts `custom_attributes ->> 'key'`, the text of the JSON value: a number or a
 * yes/no saved by the API goes as its text ("5", "false"), and "5.0" is the number 5.
 */
const castCustomValue = (value, cast) => {
  if (!['string', 'number', 'boolean'].includes(typeof value)) return null;
  const text = String(value).trim();
  switch (cast) {
    case 'numeric':
      return text === '' ? null : Number(text);
    case 'boolean': {
      const word = text.toLowerCase();
      if (word === '1' || word === '0') return word === '1';
      const isTrue = TRUE_WORDS.some(trueWord => trueWord.startsWith(word));
      const isFalse = FALSE_WORDS.some(falseWord => falseWord.startsWith(word));
      return isTrue === isFalse ? null : isTrue;
    }
    case 'date': {
      const day = writtenDay(text);
      return DAY_ONLY.test(day) ? day : null;
    }
    default:
      return String(value).toLowerCase();
  }
};

/**
 * Guesses the cast of a custom attribute from its value, when the store does not have the
 * attribute (not loaded yet)
 * @param {*} filterValue - One value of the filter
 * @param {*} conversationValue - The value of the custom attribute
 * @returns {String} - text, numeric, boolean or date
 *
 * The pill has a text field for a number attribute: it sends '5', and the conversation has 5.
 * A date attribute goes as the day ('2026-09-01'), and the conversation has a date with time.
 * Only the type of the attribute tells a number saved as text ('5.0') from a text saved as a
 * number (a zip code 1234).
 */
const guessCast = (filterValue, conversationValue) => {
  if (typeof conversationValue === 'boolean') return 'boolean';
  if (typeof conversationValue === 'number') return 'numeric';
  if (DAY_ONLY.test(filterValue) && DATE_TIME.test(conversationValue)) {
    return 'date';
  }
  return 'text';
};

/**
 * Checks if a filter value is the value of a custom attribute, both cast as the backend casts them
 * @param {*} filterValue - One value of the filter
 * @param {*} conversationValue - The value of the custom attribute
 * @param {String} [cast] - The cast of the attribute type, when the store has the attribute
 * @returns {Boolean}
 */
const sameCustomValue = (filterValue, conversationValue, cast) => {
  const as = cast || guessCast(filterValue, conversationValue);
  const value = castCustomValue(conversationValue, as);
  return value !== null && value === castCustomValue(filterValue, as);
};

/**
 * equalTo for a custom attribute: matches when any filter value is the attribute's value
 * @param {*} filterValue - The filter value, or a list of them
 * @param {*} conversationValue - The value of the custom attribute
 * @param {String} [cast] - The cast of the attribute type
 * @returns {Boolean}
 */
const equalToCustom = (filterValue, conversationValue, cast) =>
  (Array.isArray(filterValue) ? filterValue : [filterValue]).some(value =>
    sameCustomValue(value, conversationValue, cast)
  );

/**
 * "Greater than" and "less than" for a custom attribute, as the backend (lt_gt_filter_query)
 * casts the first filter value: a number as numeric, and a date by its day (Date.iso8601)
 * @param {*} conversationValue - The value of the custom attribute
 * @param {*} filterValue - The filter value, or a list of them
 * @param {String} [cast] - The cast of the attribute type
 * @param {Function} compareFn - The comparison function to apply
 * @returns {Boolean}
 *
 * compareDates took '150.50' and '100' as dates, so only whole numbers matched, by chance. The
 * backend refuses any other type, and those keep comparing as dates.
 */
const compareCustom = (conversationValue, filterValue, cast, compareFn) => {
  const valueToCompare = Array.isArray(filterValue)
    ? filterValue[0]
    : filterValue;
  if ((cast || guessCast(valueToCompare, conversationValue)) === 'numeric') {
    const value = castCustomValue(conversationValue, 'numeric');
    const target = castCustomValue(valueToCompare, 'numeric');
    return value !== null && target !== null && compareFn(value, target);
  }
  return compareDates(
    writtenDay(conversationValue),
    writtenDay(valueToCompare),
    compareFn
  );
};

// The integer Ruby's String#to_i reads: spaces, a sign, and digits with single underscores.
const RUBY_INTEGER = /^\s*([+-]?\d+(?:_\d+)*)/;

/**
 * Reads the number of days of "more than N days ago" as the backend does
 * (days_before_filter_query: `values[0].to_i`)
 * @param {*} filterValue - The filter value, or a list of them
 * @returns {Number} - The whole number of days, 0 when there is none to read
 *
 * to_i reads the integer at the start and stops at anything else: '7.5' is 7, '1e1' is 1 and
 * 'abc' is 0, so "more than 'abc' days ago" is everything before today. With a list, the
 * backend reads only the first value.
 */
const daysOf = filterValue => {
  const value = Array.isArray(filterValue) ? filterValue[0] : filterValue;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.trunc(value) : 0;
  }
  const integer = RUBY_INTEGER.exec(String(value ?? ''));
  return integer ? Number(integer[1].replaceAll('_', '')) : 0;
};

/**
 * Checks if a value matches a filter condition
 * @param {*} conversationValue - The value to check
 * @param {Object} filter - The filter condition
 * @param {Object} [casts] - The cast of each custom attribute (customAttributeCasts)
 * @returns {Boolean} - Returns true if the value matches the filter
 */
const matchesCondition = (conversationValue, filter, casts = {}) => {
  const { filter_operator: filterOperator, values } = filter;

  const isNullish =
    conversationValue === null || conversationValue === undefined;

  const filterValue = Array.isArray(values)
    ? values.map(resolveValue)
    : resolveValue(values);

  const isCustom = isCustomAttribute(filter.attribute_key);
  const equals = isCustom
    ? (value, attributeValue) =>
        equalToCustom(value, attributeValue, casts[filter.attribute_key])
    : equalTo;
  const dateValue = isCustom
    ? writtenDay(conversationValue)
    : conversationValue;
  // The backend reads a text, link or list custom attribute as `LOWER(custom_attributes ->> 'key')`,
  // the text of the JSON value: for "contains", the number 12345 saved by the API is '12345'.
  const textValue =
    isCustom &&
    casts[filter.attribute_key] === 'text' &&
    ['number', 'boolean'].includes(typeof conversationValue)
      ? String(conversationValue)
      : conversationValue;
  const compare = compareFn =>
    isCustom
      ? compareCustom(
          conversationValue,
          filterValue,
          casts[filter.attribute_key],
          compareFn
        )
      : compareDates(conversationValue, filterValue, compareFn);

  switch (filterOperator) {
    case 'equal_to':
      return equals(filterValue, conversationValue);

    case 'not_equal_to':
      return !equals(filterValue, conversationValue);

    case 'contains':
      return contains(filterValue, textValue);

    case 'does_not_contain':
      return !contains(filterValue, textValue);

    case 'is_present':
      return !isNullish;

    case 'is_not_present':
      // A conversation without labels comes with `labels: []`, and the backend's
      // `NOT EXISTS taggings` sends it: the empty list is absent too.
      return (
        isNullish ||
        (Array.isArray(conversationValue) && conversationValue.length === 0)
      );

    case 'is_greater_than':
      return compare((a, b) => a > b);

    case 'is_less_than':
      return compare((a, b) => a < b);

    case 'days_before': {
      if (isNullish) {
        return false;
      }

      // The backend sends it as "before the day N days ago" (days_before_filter_query):
      // `(created_at)::date < 'YYYY-MM-DD'`, with today in UTC (Time.zone). The dates of the
      // conversation come in seconds (_conversation.json.jbuilder), and comparing them with
      // getTime(), in milliseconds, let every conversation in. compareDates reads both.
      const today = new Date();
      const daysInMilliseconds = daysOf(filterValue) * 24 * 60 * 60 * 1000;
      const targetDate = new Date(today.getTime() - daysInMilliseconds);
      if (Number.isNaN(targetDate.getTime())) return false;
      return compareDates(
        dateValue,
        targetDate.toISOString().slice(0, 10),
        (a, b) => a < b
      );
    }

    default:
      return false;
  }
};

/**
 * Converts an array of evaluated filters into a JSON Logic rule
 * that respects SQL-like operator precedence (AND before OR)
 *
 * This function transforms the linear sequence of filter results and operators
 * into a nested JSON Logic structure that correctly implements SQL-like precedence:
 * - AND operators are evaluated before OR operators
 * - Consecutive AND conditions are grouped together
 * - These AND groups are then connected with OR operators
 *
 * For example:
 * - "A AND B AND C" becomes { "and": [A, B, C] }
 * - "A OR B OR C" becomes { "or": [A, B, C] }
 * - "A AND B OR C" becomes { "or": [{ "and": [A, B] }, C] }
 * - "A OR B AND C" becomes { "or": [A, { "and": [B, C] }] }
 *
 * FILTER CHAIN:  A --AND--> B --OR--> C --AND--> D --AND--> E --OR--> F
 *                |         |         |         |         |         |
 *                v         v         v         v         v         v
 * EVALUATED:    true      false     true      false     true      false
 *                \         /         \         \         /         /
 *                 \       /           \         \       /         /
 *                  \     /             \         \     /         /
 *                   \   /               \         \   /         /
 *                    \ /                 \         \ /         /
 * AND GROUPS:      [true,false]          [true,false,true]    [false]
 *                     |                       |                  |
 *                     v                       v                  v
 * JSON LOGIC:    {"and":[true,false]}   {"and":[true,false,true]}  false
 *                   \                      |                  /
 *                    \                     |                 /
 *                     \                    |                /
 *                      \                   |               /
 *                       \                  |              /
 * FINAL RULE:            {"or":[{"and":[true,false]},{"and":[true,false,true]},false]}
 *
 * {
 *  "or": [
 *    { "and": [true, false] },
 *    { "and": [true, false, true] },
 *    { "and": [false] }
 *  ]
 * }
 * @param {Array} evaluatedFilters - Array of evaluated filter conditions with results and operators
 * @returns {Object} - JSON Logic rule
 */
const buildJsonLogicRule = evaluatedFilters => {
  // Step 1: Group consecutive AND conditions into logical units
  // This implements the higher precedence of AND over OR
  const andGroups = [];
  let currentAndGroup = [evaluatedFilters[0].result];

  for (let i = 0; i < evaluatedFilters.length - 1; i += 1) {
    if (evaluatedFilters[i].operator === 'and') {
      // When we see an AND operator, we add the next filter to the current AND group
      // This builds up chains of AND conditions that will be evaluated together
      currentAndGroup.push(evaluatedFilters[i + 1].result);
    } else {
      // When we see an OR operator, it marks the boundary between AND groups
      // We finalize the current AND group and start a new one

      // If the AND group has only one item, don't wrap it in an "and" operator
      // Otherwise, create a proper "and" JSON Logic expression
      andGroups.push(
        currentAndGroup.length === 1
          ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
          : { and: currentAndGroup } // Multiple items need to be AND-ed together
      );

      // Start a new AND group with the next filter's result
      currentAndGroup = [evaluatedFilters[i + 1].result];
    }
  }

  // Step 2: Add the final AND group that wasn't followed by an OR
  if (currentAndGroup.length > 0) {
    andGroups.push(
      currentAndGroup.length === 1
        ? currentAndGroup[0] // Single item doesn't need an "and" wrapper
        : { and: currentAndGroup } // Multiple items need to be AND-ed together
    );
  }

  // Step 3: Combine all AND groups with OR operators
  // If we have multiple AND groups, they are separated by OR operators
  // in the original filter chain, so we combine them with an "or" operation
  if (andGroups.length > 1) {
    return { or: andGroups };
  }

  // If there's only one AND group (which might be a single condition
  // or multiple AND-ed conditions), just return it directly
  return andGroups[0];
};

/**
 * Evaluates each filter against the conversation and prepares the results array
 * @param {Object} conversation - The conversation to evaluate
 * @param {Array} filters - Filters to apply
 * @param {Object} casts - The cast of each custom attribute
 * @returns {Array} - Array of evaluated filter results with operators
 */
const evaluateFilters = (conversation, filters, casts) => {
  return filters.map((filter, index) => {
    const value = getValueFromConversation(conversation, filter.attribute_key);
    const result = matchesCondition(value, filter, casts);

    // This part determines the logical operator that connects this filter to the next one:
    // - If this is not the last filter (index < filters.length - 1), use the filter's query_operator
    //   or default to 'and' if query_operator is not specified
    // - If this is the last filter, set operator to null since there's no next filter to connect to
    const isLastFilter = index === filters.length - 1;
    const operator = isLastFilter ? null : filter.query_operator || 'and';

    return { result, operator };
  });
};

/**
 * Checks if a conversation matches the given filters
 * @param {Object} conversation - The conversation object to check
 * @param {Array} filters - Array of filter conditions
 * @param {Object} [casts] - The cast of each custom attribute (customAttributeCasts), so a custom
 *   attribute matches by its type, like the backend
 * @returns {Boolean} - Returns true if conversation matches filters, false otherwise
 */
export const matchesFilters = (conversation, filters, casts = {}) => {
  // If no filters, return true
  if (!filters || filters.length === 0) {
    return true;
  }

  // Handle single filter case
  if (filters.length === 1) {
    const value = getValueFromConversation(
      conversation,
      filters[0].attribute_key
    );

    return matchesCondition(value, filters[0], casts);
  }

  // Evaluate all conditions and prepare for jsonLogic
  const evaluatedFilters = evaluateFilters(conversation, filters, casts);
  return jsonLogic.apply(buildJsonLogicRule(evaluatedFilters));
};
